// The Bookings area's wire types — hand-authored against
// server/src/models/bookings.rs (and the HoursWindow shape in trading.rs,
// DiningTable in dine_in_delivery.rs). The shared `Booking` in lib/api.ts is
// the overview's slice; this is the whole entity.

import { ApiError } from '../../lib/api';

export type BookingStatus = 'new' | 'accepted' | 'rejected' | 'cancelled' | 'completed';
export type BookingSource = 'website' | 'pos';

export type BookingEvent = {
  seq: number;
  eventType: string;
  actorType: string;
  actorId: string | null;
  note: string | null;
  occurredAt: string;
};

export type Booking = {
  bookingId: string;
  status: BookingStatus;
  statusReason: string | null;
  source: BookingSource;
  /** YYYY-MM-DD, store-local */
  bookingDate: string;
  /** HH:MM, store-local */
  bookingTime: string;
  /** the configured session the time falls in; null outside every session */
  sessionNo: number | null;
  pax: number;
  customerId: string | null;
  customerName: string;
  customerPhoneE164: string;
  customerEmail: string | null;
  tableId: string | null;
  tableName: string | null;
  notes: string | null;
  placedAt: string;
  /** the timeline — filled on the single read, empty on the list */
  events: BookingEvent[];
  createdAt: string;
  updatedAt: string;
};

/** POST …/bookings — staff placement is source `pos` (the server decides). */
export type PlaceBookingInput = {
  bookingDate: string;
  bookingTime: string;
  pax: number;
  customerName: string;
  customerPhone: string;
  customerEmail: string | null;
  notes: string | null;
  tableId: string | null;
};

/** PUT …/bookings/{id} — every field optional; tableId "" clears the table. */
export type AmendBookingInput = {
  bookingDate?: string;
  bookingTime?: string;
  pax?: number;
  tableId?: string;
  notes?: string;
};

export type BookingSettings = {
  enabled: boolean;
  maxPaxPerBooking: number;
  capacityPerSession: number | null;
  leadMinutes: number;
  scheduleAheadDays: number;
};

/** The hours shape: ISO weekday 1–7 (Mon–Sun), session 1 or 2. */
export type BookingSession = {
  dayOfWeek: number;
  sessionNo: number;
  isOpen: boolean;
  openTime: string | null;
  closeTime: string | null;
};

export type ClosedDate = { closedOn: string; label: string | null };

/** GET/PUT …/booking-settings — read and written whole. The GET is public. */
export type BookingConfig = {
  settings: BookingSettings;
  sessions: BookingSession[];
  closedDates: ClosedDate[];
};

export type DiningTable = {
  tableId: string;
  name: string;
  maxGuests: number | null;
  active: boolean;
  occupied: boolean;
};

export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** Today's date (YYYY-MM-DD) in the store's timezone. */
export const todayIn = (tz?: string): string => new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date());

export const isLive = (s: BookingStatus): boolean => s === 'new' || s === 'accepted';

/** A refusal as the page shows it: the server's message, its typed code. */
export type Refusal = { code: string; message: string };

export const refusalOf = (e: unknown, fallback: string): Refusal =>
  e instanceof ApiError ? { code: e.code, message: e.message } : { code: 'unknown', message: e instanceof Error ? e.message : fallback };
